import { useState, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  useOrder,
  useConstructionTeams,
  useCreateConstructionOrder,
} from '@saiens/shared/hooks';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import {
  ArrowLeft,
  User,
  MapPin,
  FileText,
  Users,
  Square,
  Package,
  Wrench,
  MoreHorizontal,
  HardHat,
  Check,
} from 'lucide-react';
import type { SaleOrderLine } from '@saiens/shared/types';

interface LineItemWithSelection extends SaleOrderLine {
  selected: boolean;
}

interface LineSectionProps {
  title: string;
  icon: React.ReactNode;
  items: LineItemWithSelection[];
  onToggle: (id: number) => void;
  onSelectAll: () => void;
}

function LineSection({ title, icon, items, onToggle, onSelectAll }: LineSectionProps) {
  if (items.length === 0) return null;

  const selectedCount = items.filter((item) => item.selected).length;
  const allSelected = selectedCount === items.length;

  return (
    <div className="rounded-xl bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-gray-100 p-4">
        <div className="flex items-center gap-2">
          <span className="h-5 w-5 text-gray-500">{icon}</span>
          <span className="font-semibold text-gray-900">{title}</span>
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
            {items.length}
          </span>
        </div>
        <button
          onClick={onSelectAll}
          className={`text-sm ${allSelected ? 'text-primary' : 'text-gray-500'}`}
        >
          {allSelected ? '取消全選' : '全選'}
        </button>
      </div>
      <div className="divide-y divide-gray-50 px-4">
        {items.map((item) => (
          <label
            key={item.id}
            className="flex cursor-pointer items-start gap-3 py-3"
          >
            <div
              className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border ${
                item.selected
                  ? 'border-primary bg-primary'
                  : 'border-gray-300 bg-white'
              }`}
            >
              {item.selected && <Check className="h-3 w-3 text-white" />}
            </div>
            <input
              type="checkbox"
              checked={item.selected}
              onChange={() => onToggle(item.id)}
              className="sr-only"
              aria-label={`選取 ${item.product_name || item.name || '項目'}`}
            />
            <div className="min-w-0 flex-1">
              <p className="font-medium text-gray-900">
                {item.product_name || item.name || '未命名項目'}
              </p>
              <div className="mt-1 flex flex-wrap gap-2 text-xs text-gray-500">
                {item.saiens_color && (
                  <span className="rounded bg-gray-100 px-1.5 py-0.5">
                    {item.saiens_color}
                  </span>
                )}
                {item.length > 0 && item.depth > 0 && (
                  <span className="rounded bg-gray-100 px-1.5 py-0.5">
                    {item.length} x {item.depth}
                  </span>
                )}
                {item.standard && (
                  <span className="rounded bg-gray-100 px-1.5 py-0.5">
                    {item.standard}
                  </span>
                )}
                <span className="rounded bg-blue-50 px-1.5 py-0.5 text-blue-600">
                  x{item.product_uom_qty}
                </span>
              </div>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}

export function ConstructionCreatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const saleOrderIdParam = searchParams.get('saleOrderId');
  const saleOrderId = saleOrderIdParam
    ? (Number.isNaN(parseInt(saleOrderIdParam, 10)) ? undefined : parseInt(saleOrderIdParam, 10))
    : undefined;

  const { data: order, isLoading: orderLoading, error: orderError } = useOrder(saleOrderId);
  const { data: teams, isLoading: teamsLoading } = useConstructionTeams();
  const createConstruction = useCreateConstructionOrder();

  const [selectedTeamIds, setSelectedTeamIds] = useState<number[]>([]);
  const [selectedLineIds, setSelectedLineIds] = useState<number[]>([]);

  // Initialize line items with selection state
  const lineItems = useMemo(() => {
    if (!order) return { line1: [], line2: [], line3: [], line4: [] };

    const mapWithSelection = (items: SaleOrderLine[]): LineItemWithSelection[] =>
      items.map((item) => ({
        ...item,
        selected: selectedLineIds.includes(item.id),
      }));

    return {
      line1: mapWithSelection(order.order_line_1 || []),
      line2: mapWithSelection(order.order_line_2 || []),
      line3: mapWithSelection(order.order_line_3 || []),
      line4: mapWithSelection(order.order_line_4 || []),
    };
  }, [order, selectedLineIds]);

  const totalItems =
    lineItems.line1.length +
    lineItems.line2.length +
    lineItems.line3.length +
    lineItems.line4.length;

  const toggleTeam = (id: number) => {
    setSelectedTeamIds((prev) =>
      prev.includes(id) ? prev.filter((tid) => tid !== id) : [...prev, id]
    );
  };

  const toggleLine = (id: number) => {
    setSelectedLineIds((prev) =>
      prev.includes(id) ? prev.filter((lid) => lid !== id) : [...prev, id]
    );
  };

  const selectAllInSection = (items: SaleOrderLine[]) => {
    const ids = items.map((item) => item.id);
    const allSelected = ids.every((id) => selectedLineIds.includes(id));

    if (allSelected) {
      setSelectedLineIds((prev) => prev.filter((id) => !ids.includes(id)));
    } else {
      setSelectedLineIds((prev) => [...new Set([...prev, ...ids])]);
    }
  };

  const handleSubmit = () => {
    if (!saleOrderId || selectedLineIds.length === 0) return;

    createConstruction.mutate(
      {
        sale_order_id: saleOrderId,
        construct_team_ids: selectedTeamIds,
        sale_line_ids: selectedLineIds,
      },
      {
        onSuccess: () => {
          navigate('/construction');
        },
      }
    );
  };

  if (!saleOrderId) {
    return (
      <div className="px-4 py-6">
        <Link to="/crm" className="flex items-center gap-1 text-primary">
          <ArrowLeft className="h-4 w-4" /> 返回
        </Link>
        <div className="mt-8 text-center text-red-500">缺少報價單 ID</div>
      </div>
    );
  }

  if (orderLoading || teamsLoading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (orderError || !order) {
    return (
      <div className="px-4 py-6">
        <Link to="/crm" className="flex items-center gap-1 text-primary">
          <ArrowLeft className="h-4 w-4" /> 返回
        </Link>
        <div className="mt-8 text-center text-red-500">載入失敗或報價單不存在</div>
      </div>
    );
  }

  return (
    <div className="pb-32 px-4 py-6">
      {/* Header */}
      <div className="mb-6 flex items-center gap-3">
        <Link to="/crm" className="flex items-center text-primary">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <h1 className="text-xl font-bold text-gray-900">建立施工單</h1>
      </div>

      {/* Order Info Card */}
      <div className="mb-4 rounded-xl bg-white p-4 shadow-sm">
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-gray-600">
            <FileText className="h-4 w-4" />
            <span className="text-sm">報價單:</span>
            <span className="font-medium text-gray-900">{order.name}</span>
          </div>
          {order.partner_name && (
            <div className="flex items-center gap-2 text-gray-600">
              <User className="h-4 w-4" />
              <span className="text-sm">客戶:</span>
              <span className="font-medium text-gray-900">{order.partner_name}</span>
            </div>
          )}
          {order.project_addr && (
            <div className="flex items-center gap-2 text-gray-600">
              <MapPin className="h-4 w-4" />
              <span className="text-sm">地址:</span>
              <span className="font-medium text-gray-900">{order.project_addr}</span>
            </div>
          )}
        </div>
      </div>

      {/* Team Selection */}
      <div className="mb-4 rounded-xl bg-white p-4 shadow-sm">
        <div className="mb-3 flex items-center gap-2">
          <Users className="h-5 w-5 text-gray-500" />
          <h2 className="font-semibold text-gray-900">安裝團隊</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          {teams?.map((member) => (
            <label
              key={member.id}
              className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 ${
                selectedTeamIds.includes(member.id)
                  ? 'border-primary bg-primary/5 text-primary'
                  : 'border-gray-200 bg-white text-gray-700'
              }`}
            >
              <input
                type="checkbox"
                checked={selectedTeamIds.includes(member.id)}
                onChange={() => toggleTeam(member.id)}
                className="sr-only"
                aria-label={`選取團隊成員 ${member.name}`}
              />
              <div
                className={`flex h-4 w-4 items-center justify-center rounded border ${
                  selectedTeamIds.includes(member.id)
                    ? 'border-primary bg-primary'
                    : 'border-gray-300 bg-white'
                }`}
              >
                {selectedTeamIds.includes(member.id) && (
                  <Check className="h-3 w-3 text-white" />
                )}
              </div>
              <span className="text-sm">{member.name}</span>
            </label>
          ))}
          {(!teams || teams.length === 0) && (
            <p className="text-sm text-gray-500">無可用團隊成員</p>
          )}
        </div>
      </div>

      {/* Line Items */}
      <div className="space-y-4">
        <LineSection
          title="檯面項目"
          icon={<Square />}
          items={lineItems.line1}
          onToggle={toggleLine}
          onSelectAll={() => selectAllInSection(order.order_line_1 || [])}
        />

        <LineSection
          title="材料項目"
          icon={<Package />}
          items={lineItems.line2}
          onToggle={toggleLine}
          onSelectAll={() => selectAllInSection(order.order_line_2 || [])}
        />

        <LineSection
          title="加工項目"
          icon={<Wrench />}
          items={lineItems.line3}
          onToggle={toggleLine}
          onSelectAll={() => selectAllInSection(order.order_line_3 || [])}
        />

        <LineSection
          title="其他項目"
          icon={<MoreHorizontal />}
          items={lineItems.line4}
          onToggle={toggleLine}
          onSelectAll={() => selectAllInSection(order.order_line_4 || [])}
        />
      </div>

      {totalItems === 0 && (
        <div className="mt-4 rounded-xl bg-yellow-50 p-4 text-center text-sm text-yellow-700">
          此報價單沒有可選的項目
        </div>
      )}

      {/* Fixed Bottom Button */}
      <div className="fixed bottom-20 left-0 right-0 z-40 border-t bg-white p-4">
        <button
          onClick={handleSubmit}
          disabled={createConstruction.isPending || selectedLineIds.length === 0}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3 font-medium text-white hover:bg-primary/90 disabled:bg-gray-300"
        >
          {createConstruction.isPending ? (
            <LoadingSpinner size="sm" />
          ) : (
            <>
              <HardHat className="h-5 w-5" />
              建立施工單 (已選 {selectedLineIds.length} 項)
            </>
          )}
        </button>
      </div>
    </div>
  );
}
